// Tracks the FilterRail collapsed/expanded state.
//
//  - localStorage key `inpact:rail-collapsed` (string "1" | "0") for persist
//  - mobile (< 720px) defaults to collapsed and never persists the auto-
//    collapse (so a desktop preference survives a phone visit)
//  - exposes a `data-rail` attribute on document.documentElement so the CSS
//    grid in components.css can react without prop-drilling through
//    ExplorerView.

import { useCallback, useEffect, useState } from "react";

const LS_KEY = "inpact:rail-collapsed";
const MOBILE_BP = 720;

function readPersisted(): boolean | null {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw === "1") return true;
    if (raw === "0") return false;
    return null;
  } catch {
    return null;
  }
}

function detectMobile(): boolean {
  if (typeof window === "undefined") return false;
  if (typeof window.matchMedia !== "function") return false;
  return window.matchMedia(`(max-width: ${MOBILE_BP - 1}px)`).matches;
}

export function useRailCollapse() {
  const [isMobile, setIsMobile] = useState<boolean>(detectMobile);
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    const persisted = readPersisted();
    if (persisted !== null) return persisted;
    return detectMobile();
  });

  // Track viewport so mobile auto-collapses without overwriting desktop pref.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia(`(max-width: ${MOBILE_BP - 1}px)`);
    const onChange = (e: MediaQueryListEvent) => {
      setIsMobile(e.matches);
      if (e.matches) setCollapsed(true);
      else {
        const persisted = readPersisted();
        if (persisted !== null) setCollapsed(persisted);
      }
    };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  // Mirror state to document so CSS can size the grid.
  useEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.dataset.rail = collapsed ? "collapsed" : "expanded";
  }, [collapsed]);

  const toggle = useCallback(() => {
    setCollapsed((c) => {
      const next = !c;
      try { localStorage.setItem(LS_KEY, next ? "1" : "0"); } catch { /* ignore */ }
      return next;
    });
  }, []);

  return { collapsed, isMobile, toggle };
}
